import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import {
  SdTestAppBenchClient,
  type BenchFirmwareSuite,
  type BenchFirmwareSuiteResult,
} from "@/lib/sdtestapp/sdTestAppBenchClient";

function FirmwareSuiteResultCard({ result }: { result: BenchFirmwareSuiteResult }): JSX.Element {
  return (
    <article className="rounded border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={result.passed ? "success" : "danger"}>{result.overallLabel}</Badge>
        <span className="font-medium">{result.suiteName}</span>
        {result.firmwareVersion ? (
          <span className="text-xs text-muted-foreground">Firmware {result.firmwareVersion}</span>
        ) : null}
      </div>
      {result.errorMessage ? <p className="mt-2 text-sm text-destructive">{result.errorMessage}</p> : null}
      {result.steps.length > 0 ? (
        <ul className="mt-3 space-y-2 text-sm">
          {result.steps.map((step) => (
            <li key={step.stepName} className="rounded border px-2 py-1">
              <div className="flex items-center gap-2">
                <Badge tone={step.passed ? "success" : "danger"}>
                  {step.label}
                </Badge>
                <span className="font-medium">{step.stepName}</span>
              </div>
              {step.message ? <p className="mt-1 text-muted-foreground">{step.message}</p> : null}
              {step.detail ? <p className="mt-1 font-mono text-xs text-muted-foreground">{step.detail}</p> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </article>
  );
}

export function BenchFirmwareTab({
  port,
  machineId,
  connectorId,
  canActuate,
  observeOnly = false,
}: {
  port: string;
  machineId?: string;
  connectorId: string | null;
  canActuate: boolean;
  observeOnly?: boolean;
}): JSX.Element {
  const client = useMemo(() => new SdTestAppBenchClient(port), [port]);
  const [suites, setSuites] = useState<BenchFirmwareSuite[]>([]);
  const [selectedSuiteId, setSelectedSuiteId] = useState("");
  const [results, setResults] = useState<BenchFirmwareSuiteResult[]>([]);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const rows =
          observeOnly && machineId
            ? await SdTestAppBenchClient.relayFirmwareSuites(machineId)
            : await client.fetchFirmwareSuites();
        setSuites(rows);
        if (rows[0]) setSelectedSuiteId(rows[0].suiteId);
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : String(loadError));
      }
    };
    if (observeOnly && machineId) {
      void load();
      return undefined;
    }
    if (!canActuate) return undefined;
    void load();
    return undefined;
  }, [canActuate, client, machineId, observeOnly]);

  const runAction = async (action: () => Promise<void>) => {
    if (!connectorId) return;
    setPending(true);
    setError(null);
    try {
      await action();
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : String(actionError));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Run curated firmware validation suites against the selected device using SDTestApp command sessions.
      </p>
      {observeOnly ? (
        <p className="text-sm text-muted-foreground">Suite catalog is read-only via relay observe. Run tests locally on the bench PC.</p>
      ) : null}
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[260px]">
          <label className="text-sm font-medium" htmlFor="bench-firmware-suite">
            Suite
          </label>
          <select
            id="bench-firmware-suite"
            className="mt-1 block w-full rounded border px-2 py-1 text-sm"
            value={selectedSuiteId}
            onChange={(event) => setSelectedSuiteId(event.target.value)}
          >
            {suites.map((suite) => (
              <option key={suite.suiteId} value={suite.suiteId}>
                {suite.name}
              </option>
            ))}
          </select>
        </div>
        {!observeOnly ? (
          <>
            <Button
              type="button"
              disabled={!canActuate || !connectorId || !selectedSuiteId || pending}
              onClick={() => void runAction(async () => {
                const payload = await client.runFirmwareSuite(selectedSuiteId, connectorId!);
                setResults([payload.result]);
              })}
            >
              Run suite
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={!canActuate || !connectorId || pending}
              onClick={() => void runAction(async () => {
                const payload = await client.runAllFirmwareSuites(connectorId!);
                setResults(payload.results);
              })}
            >
              Run all suites
            </Button>
          </>
        ) : null}
      </div>
      {!connectorId ? <Badge tone="warning">Select a device on the Main tab first.</Badge> : null}
      {pending ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Running suite…
        </p>
      ) : null}
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      {results.length > 0 ? (
        <div className="space-y-3">
          {results.map((result) => (
            <FirmwareSuiteResultCard key={`${result.suiteId}-${result.suiteName}`} result={result} />
          ))}
        </div>
      ) : null}
    </div>
  );
}
